import api, { ApiResponse } from './index'
import { Gig, CreateGigData, UpdateGigData, GigFilters, GigCategory, GigStats } from '@/types/gig'
import { accessLimitService } from '../accessLimitService'

export const gigApi = {
    // Get gigs with optional filters and pagination
    getGigs: (params: {
        page?: number
        limit?: number
        filters?: Partial<GigFilters>
    } = {}): Promise<ApiResponse<{
        gigs: Gig[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> => {
        // Flatten filters with other params for the API call
        const { filters, ...otherParams } = params
        const flattenedParams = { ...otherParams, ...filters }
        return api.get('/gigs', { params: flattenedParams })
    },

    // Get single gig by ID
    getGigById: (id: string): Promise<ApiResponse<{ gig: Gig }>> =>
        api.get(`/gigs/${id}`),

    // Search gigs
    searchGigs: (params: {
        query: string
        filters?: Partial<GigFilters>
        page?: number
        limit?: number
    }): Promise<ApiResponse<{
        gigs: Gig[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> => {
        // Flatten filters with other params for the API call
        const { filters, ...otherParams } = params
        const flattenedParams = { ...otherParams, ...filters }
        return api.get('/gigs/search', { params: flattenedParams })
    },

    // Get featured gigs
    getFeaturedGigs: (limit?: number): Promise<ApiResponse<{ gigs: Gig[] }>> =>
        api.get('/gigs/featured', { params: { limit } }),

    // Get nearby gigs based on coordinates
    getNearbyGigs: (params: {
        coordinates: [number, number]
        radius?: number
        limit?: number
    }): Promise<ApiResponse<{ gigs: Gig[] }>> =>
        api.get('/gigs/nearby', { params }),

    // Get user's own gigs (for employers)
    getUserGigs: (params: {
        page?: number
        limit?: number
        status?: string
        moderationStatus?: string
    } = {}): Promise<ApiResponse<{
        gigs: Gig[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/gigs/user/my-gigs', { params }),

    // Create new gig
    createGig: async (gigData: CreateGigData): Promise<ApiResponse<{ gig: Gig }>> => {
        try {
            return await api.post('/gigs', gigData);
        } catch (error: any) {
            // Check if this is an access limit error and enhance the error message
            const accessLimitError = accessLimitService.handleAccessLimitError(error);
            if (accessLimitError.isAccessLimitError) {
                // Add enhanced error information for the frontend to handle
                error.accessLimitError = accessLimitError;
            }
            throw error;
        }
    },

    // Update existing gig
    updateGig: (id: string, data: UpdateGigData): Promise<ApiResponse<{ gig: Gig }>> =>
        api.put(`/gigs/${id}`, data),

    // Delete gig
    deleteGig: (id: string): Promise<ApiResponse> =>
        api.delete(`/gigs/${id}`),

    // Reactivate expired gig
    reactivateGig: (id: string, newEndDate?: string): Promise<ApiResponse<{ gig: Gig }>> =>
        api.post(`/gigs/${id}/reactivate`, { newEndDate }),

    // Get gig categories
    getCategories: (): Promise<ApiResponse<{ categories: GigCategory[] }>> =>
        api.get('/gigs/categories'),

    // Get gig statistics
    getGigStats: (userId?: string): Promise<ApiResponse<{ stats: GigStats }>> =>
        api.get('/gigs/stats', { params: { userId } }),

    // Apply to gig
    applyToGig: async (gigId: string, applicationData: {
        message: string
        proposedRate?: number
        estimatedDuration?: string
        availability?: string
        attachments?: string[]
    }): Promise<ApiResponse<{ application: any }>> => {
        try {
            return await api.post(`/gigs/${gigId}/apply`, applicationData);
        } catch (error: any) {
            // Check if this is an access limit error and enhance the error message
            const accessLimitError = accessLimitService.handleAccessLimitError(error);
            if (accessLimitError.isAccessLimitError) {
                // Add enhanced error information for the frontend to handle
                error.accessLimitError = accessLimitError;
            }
            throw error;
        }
    },

    // Get gig applications (for gig owners)
    getGigApplications: (gigId: string, params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<{
        applications: any[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/gigs/${gigId}/applications`, { params }),

    // Toggle gig favorite
    toggleFavorite: (gigId: string): Promise<ApiResponse<{ isFavorited: boolean }>> =>
        api.post(`/gigs/${gigId}/favorite`),

    // Get user's favorite gigs
    getFavoriteGigs: (params: {
        page?: number
        limit?: number
    } = {}): Promise<ApiResponse<{
        gigs: Gig[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/gigs/user/favorites', { params }),

    // Report gig
    reportGig: (gigId: string, reason: string, description?: string): Promise<ApiResponse> =>
        api.post(`/gigs/${gigId}/report`, { reason, description }),

    // Mark gig as viewed
    markAsViewed: (gigId: string): Promise<ApiResponse> =>
        api.post(`/gigs/${gigId}/view`),

    // Get similar gigs
    getSimilarGigs: (gigId: string, limit?: number): Promise<ApiResponse<{ gigs: Gig[] }>> =>
        api.get(`/gigs/${gigId}/similar`, { params: { limit } }),

    // Feature gig (premium feature)
    featureGig: (gigId: string, duration: number): Promise<ApiResponse<{ gig: Gig }>> =>
        api.post(`/gigs/${gigId}/feature`, { duration }),

    // Get gig analytics (for gig owners)
    getGigAnalytics: (gigId: string, dateRange?: {
        start: string
        end: string
    }): Promise<ApiResponse<{
        views: number
        applications: number
        viewsOverTime: Array<{ date: string; views: number }>
        applicationsOverTime: Array<{ date: string; applications: number }>
    }>> =>
        api.get(`/gigs/${gigId}/analytics`, { params: dateRange }),
}